import React, { memo, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { View, EmptyState, ListGroup, ListGroupControl, LoadingState } from "components/Shared";
import { translate } from "lib";
import { getForms, deleteForm, uploadFormFileToS3, postForm, getFormUploadUrl } from "actions/forms";
import { startUploadTray } from "actions/documents";
import axios from "axios";
import Item from "./Item";
import Download from "./Download";
import FormsPagination from "./FormsPagination";

interface Props {
	name?: any;
	property: any;
	isAdministrator?: boolean;
	// Read off the element by Shared/Tabs.tsx to draw the padlock in the tab
	// label, not by this component. Declared so Properties/Show.tsx can pass it.
	icon?: string;
}

const PropertyFormsTab = ({ property, isAdministrator = true }: Props) => {
	const dispatch = useDispatch();
	const allForms = useSelector((state: any) => state.forms as any[]);
	const fileRef = useRef<HTMLInputElement>(null);
	const dropRef = useRef<HTMLDivElement>(null);
	const [viewBy, setViewBy] = useState("grid");
	const [filteredBy, setFilteredBy] = useState("all");
	const [sortBy, setSortBy] = useState("created_at");
	const [perPage, setPerPage] = useState(20);
	const [page, setPage] = useState(1); // 1-indexed, mirrors the Documents tab
	const [uploading, setUploading] = useState(false);
	const [loaded, setLoaded] = useState(false);

	const propertyId = property?.id;

	useEffect(() => {
		if (!propertyId) return;
		const category = filteredBy !== "all" ? filteredBy : undefined;
		dispatch(getForms({ propertyId, category }) as any);
		setLoaded(true);
	}, [dispatch, propertyId, filteredBy]);

	// F1: Defensive filter — property Forms tab MUST NOT show snapshot forms
	// (parent_form_id non-null). The backend already scopes to templates only
	// (Form.templates scope), but we filter here as defense-in-depth so a
	// stale cache or a rogue API response can't leak snapshots into the UI.
	// Contract: api-contract.md §5 and react-frontend-tasks.md F1.
	const forms = _.orderBy(
		(allForms || []).filter(
			(f) => !f.deleted_at && !f.parent_form_id && _.get(f, "property.id") === propertyId
		),
		[(f: any) => (sortBy === "name" ? (f.name || "").toLowerCase() : f.created_at)],
		[sortBy === "name" ? "asc" : "desc"]
	);

	// Client-side pagination (forms are loaded in full via redux). Clamp the page
	// so deleting items or changing page size never leaves us on an empty page.
	const totalCount = forms.length;
	const totalPage = Math.max(1, Math.ceil(totalCount / perPage));
	const safePage = Math.min(page, totalPage);
	const pagedForms = forms.slice((safePage - 1) * perPage, safePage * perPage);

	// Reset to the first page whenever the filter/sort/page-size changes.
	useEffect(() => {
		setPage(1);
	}, [filteredBy, sortBy, perPage]);

	const onDelete = (id: string) => {
		if (!window.confirm(translate("confirm_delete"))) return;
		dispatch(deleteForm(id) as any);
	};

	// Uploads a single .docx UPLOAD-FIRST: presigned POST, file to S3, and only
	// then create the Form WITH its file_uri. The old order created the Form
	// first, so a failed upload left a fileless Form on this tab (a card with no
	// size) that could still be picked for a condition. Throws on failure so the
	// caller can report per-file errors — and nothing has been created by then.
	const uploadOne = async (file: File) => {
		const base = file.name.replace(/\.[^/.]+$/, "");
		// Append a short timestamp suffix so re-uploads of the same filename don't collide on code uniqueness.
		const slug = base.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 30) || "form";
		const code = `${slug}-${Date.now().toString(36)}`;
		// The same progress tray as a Documents upload, with its Cancel button.
		const tray = startUploadTray(dispatch, file.name);
		try {
			// 1. Presigned POST — no Form yet
			const presigned: any = await dispatch(getFormUploadUrl() as any);
			if (!presigned || !presigned.url) throw new Error("Missing presigned upload URL");
			// 2. Upload the file to S3
			const key = await uploadFormFileToS3(presigned, file, {
				cancelToken: tray.cancelToken,
				onUploadProgress: tray.onUploadProgress,
			});
			// 3. Create the Form with the uploaded file
			await createForm(key);
			tray.done();
		} catch (err) {
			if (axios.isCancel(err)) tray.cancelled(err);
			else tray.failed(err);
			throw err;
		}

		async function createForm(key: string) {
			await dispatch(
				postForm({
					name: base,
					code,
					category: "Form",
					mimetype: file.type || "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
					filesize_bytes: file.size,
					property_id: propertyId,
					placeholders: [],
					file_uri: key,
				}) as any
			);
		}
	};

	// Closing the tab or reloading mid-upload loses the file — ask first. Nothing
	// is left half-made either way, since the Form is only created once S3 has it.
	useEffect(() => {
		if (!uploading) return;
		const warn = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
			return "";
		};
		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [uploading]);

	// Uploads a batch of files sequentially: each postForm round-trip advances
	// Date.now(), keeping the generated `code` suffixes unique, and it avoids
	// hammering S3 in parallel. Shared by the file input and drag-and-drop.
	const uploadFiles = async (files: File[]) => {
		if (!files.length) return;
		setUploading(true);
		const failures: string[] = [];
		for (const file of files) {
			try {
				await uploadOne(file);
			} catch (err) {
				// Cancelled from the tray — the user asked for it, not a failure.
				if (axios.isCancel(err)) continue;
				// eslint-disable-next-line no-console
				console.error("Form upload failed", err);
				const e = err as any;
				const detail =
					e?.response?.data?.errors?.detail ||
					e?.response?.data?.errors?.[0]?.detail ||
					e?.message ||
					"Upload failed.";
				failures.push(`${file.name}: ${Array.isArray(detail) ? detail.join(", ") : detail}`);
			}
		}
		dispatch(getForms({ propertyId }) as any);
		setUploading(false);
		if (fileRef.current) fileRef.current.value = "";
		if (failures.length) window.alert(`Some uploads failed:\n${failures.join("\n")}`);
	};

	const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
		uploadFiles(e.target.files ? Array.from(e.target.files) : []);
	};

	// Drag-and-drop upload (mirrors the Documents tab). Forms are .docx-only, so
	// non-.docx drops are skipped with a heads-up.
	const isDocx = (file: File) =>
		/\.docx$/i.test(file.name) ||
		file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

	const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		if (!isAdministrator) return;
		e.preventDefault();
		dropRef.current?.classList.add("on-dragover");
	};

	const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		dropRef.current?.classList.remove("on-dragover");
	};

	const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		dropRef.current?.classList.remove("on-dragover");
		if (!isAdministrator) return;
		const dropped = e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : [];
		const docx = dropped.filter(isDocx);
		const skipped = dropped.length - docx.length;
		if (skipped > 0) window.alert(`Only .docx forms can be uploaded. Skipped ${skipped} file(s).`);
		uploadFiles(docx);
	};

	// Forms with an uploaded .docx are the only ones that can be added to the
	// zip; filter so the modal doesn't show empty form templates.
	const downloadableForms = forms.filter((f) => !!f.file_uri);

	// A LIST WITHHELD, NOT A TAB THAT VANISHES (Sim, 22 Sep 2026). Properties/Show
	// used to render this tab only for a property administrator, so a flagged
	// non-admin found it simply absent and had nothing to ask about. It is now
	// rendered like Tenancies, Tenants and Leases — padlocked, and saying why —
	// and this is the half that says why.
	//
	// `isAdministrator` is membership of `property.users`, the same set
	// PropertyPolicy#administrator? checks server-side, computed once in
	// Properties/Show.
	//
	// Declared AFTER the hooks above, never before: an early return that skips a
	// hook changes the hook order between renders.
	if (!isAdministrator) {
		return (
			<View resource="forms" hideTitle={true}>
				<EmptyState>
					<div>{translate("forms_restricted_to_property_admin")}</div>
				</EmptyState>
			</View>
		);
	}

	return (
		<View resource="forms" hideTitle={true}>
			<div
				className="drop-target"
				ref={dropRef}
				onDrop={onDrop}
				onDragOver={onDragOver}
				onDragLeave={onDragLeave}
				data-role="drag-drop-container"
			>
				<div className="documents-tool">
					{!!isAdministrator && (
						<div className="upload">
							<input
								type="file"
								ref={fileRef}
								className="file-input"
								multiple
								accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
								onChange={onFilePicked}
							/>
							<label
								onClick={() => fileRef.current?.click()}
								className="btn btn-primary btn-sm button"
								style={{ cursor: "pointer" }}
							>
								{uploading ? translate("uploading") : "Upload Forms (.docx)"}
							</label>
						</div>
					)}
					{downloadableForms.length > 0 && <Download forms={downloadableForms} />}
				</div>

				<ListGroupControl
					selectedFilter={filteredBy}
					filterOptions={["all", "Form", "Letter", "Agreement", "Notice", "Other"]}
					sortOptions={["created_at", "name"]}
					onViewChange={setViewBy}
					selectedView={viewBy}
					viewOptions={["list", "grid"]}
					onSortChange={setSortBy}
					selectedSort={sortBy}
					onFilterChange={setFilteredBy}
				/>

				{!loaded && <LoadingState />}
				<ListGroup
					resource="forms"
					collection={pagedForms}
					pagination={false}
					renderRow={(form: any) => (
						<Item
							key={form.id}
							form={form}
							onRemove={isAdministrator ? () => onDelete(form.id) : undefined}
						/>
					)}
					className={classNames("documents", "document-list-group", `documents-${viewBy}-view`)}
				/>
				<div className={classNames("margin-atuo", "flex justify-center")}>
					<FormsPagination
						totalCount={totalCount}
						currentPage={safePage}
						perPage={perPage}
						onPageChange={setPage}
						onPerPageChange={setPerPage}
					/>
				</div>
			</div>
		</View>
	);
};

export default memo(PropertyFormsTab);
